import { useId, useState } from 'react'
import { inZone, zeitInZone } from '@famora/core/recurrence/zeit'
import type { Regel } from '@famora/core/recurrence/typen'
import { useQueryClient } from '@tanstack/react-query'
import { schicke } from '../lib/api'
import { tageAddieren } from '../lib/datum'
import { useDarf, useSitzung } from '../session/SessionProvider'
import { PersonenAuswahl } from './PersonenAuswahl'
import { Button, Chip, Select, Sheet, Switch, TextField, useConfirm } from '../ui'

interface AufgabenFelder {
  kind: 'todo' | 'chore'
  dueOn: string | null
  dueTime: string | null
  priority: number
  visibility: string
  wiederholung: Regel | null
}

interface KalenderFelder {
  occurrenceDate: string
  displayDate: string
  startsAt: string | null
  endsAt: string | null
  allDayOn: string | null
  allDayBis: string | null
  location: string | null
  visibility: string
  wiederholung: Regel | null
  timezone: string
}

type Umfang = 'alle' | 'diese' | 'folgende'
const WOCHENTAGE = [
  [1, 'Mo'],
  [2, 'Di'],
  [3, 'Mi'],
  [4, 'Do'],
  [5, 'Fr'],
  [6, 'Sa'],
  [7, 'So'],
] as const

export function EintragAktionen({
  modul,
  id,
  titel,
  notizen,
  serie,
  personen = [],
  aufgabe,
  kalender,
}: {
  modul: 'tasks' | 'calendar'
  id: string
  titel: string
  notizen: string | null
  serie: boolean
  personen?: string[]
  aufgabe?: AufgabenFelder
  kalender?: KalenderFelder
}) {
  const qc = useQueryClient()
  const bestaetige = useConfirm()
  const feldId = useId()
  const { abfrage } = useSitzung()
  const edit = useDarf(`${modul}.edit`)
  const remove = useDarf(`${modul}.delete`)
  const [offen, setOffen] = useState(false)
  const [laeuft, setLaeuft] = useState(false)
  const [fehler, setFehler] = useState<string | null>(null)
  const [name, setName] = useState(titel)
  const [notes, setNotes] = useState(notizen ?? '')
  const [auswahl, setAuswahl] = useState(personen)
  const [art, setArt] = useState(aufgabe?.kind ?? 'todo')
  const [datum, setDatum] = useState(startDatum(aufgabe, kalender))
  const [zeit, setZeit] = useState(startZeit(aufgabe, kalender))
  const [dauer, setDauer] = useState(dauerMinuten(kalender))
  const [ganztaegig, setGanztaegig] = useState(Boolean(kalender?.allDayOn))
  const [bisDatum, setBisDatum] = useState(kalender?.allDayBis ?? '')
  const [ort, setOrt] = useState(kalender?.location ?? '')
  const [prioritaet, setPrioritaet] = useState(aufgabe?.priority ?? 0)
  const [sichtbarkeit, setSichtbarkeit] = useState(
    aufgabe?.visibility ?? kalender?.visibility ?? 'household',
  )

  function datumAendern(neu: string) {
    if (ganztaegig && datum && bisDatum && neu) {
      const dauerTage = Math.max(
        0,
        Math.round(
          (Date.parse(`${bisDatum}T00:00:00Z`) - Date.parse(`${datum}T00:00:00Z`)) / 86_400_000,
        ),
      )
      setBisDatum(tageAddieren(neu, dauerTage))
    }
    setDatum(neu)
  }
  const [umfang, setUmfang] = useState<Umfang>('alle')
  const [freq, setFreq] = useState(
    aufgabe?.wiederholung?.freq ?? kalender?.wiederholung?.freq ?? '',
  )
  const [intervall, setIntervall] = useState(
    aufgabe?.wiederholung?.interval ?? kalender?.wiederholung?.interval ?? 1,
  )
  const [wochentage, setWochentage] = useState<number[]>(
    aufgabe?.wiederholung?.byweekday ?? kalender?.wiederholung?.byweekday ?? [],
  )
  const [monatstag, setMonatstag] = useState<number | ''>(
    aufgabe?.wiederholung?.bymonthday?.[0] ?? kalender?.wiederholung?.bymonthday?.[0] ?? '',
  )
  const [position, setPosition] = useState<number | ''>(
    aufgabe?.wiederholung?.bysetpos ?? kalender?.wiederholung?.bysetpos ?? '',
  )
  const regel = aufgabe?.wiederholung ?? kalender?.wiederholung
  const [ende, setEnde] = useState<'nie' | 'datum' | 'anzahl'>(
    regel?.until ? 'datum' : regel?.count ? 'anzahl' : 'nie',
  )
  const [until, setUntil] = useState(regel?.until ?? '')
  const [count, setCount] = useState(regel?.count ?? 1)
  const preview = abfrage.data && !abfrage.data.setupRequired && abfrage.data.preview
  if (preview || (!edit && !remove)) return null

  function detailsUmschalten() {
    if (offen) {
      setOffen(false)
      return
    }
    const aktuelleRegel = aufgabe?.wiederholung ?? kalender?.wiederholung
    setName(titel)
    setNotes(notizen ?? '')
    setAuswahl(personen)
    setArt(aufgabe?.kind ?? 'todo')
    setDatum(startDatum(aufgabe, kalender))
    setZeit(startZeit(aufgabe, kalender))
    setDauer(dauerMinuten(kalender))
    setGanztaegig(Boolean(kalender?.allDayOn))
    setBisDatum(kalender?.allDayBis ?? '')
    setOrt(kalender?.location ?? '')
    setPrioritaet(aufgabe?.priority ?? 0)
    setSichtbarkeit(aufgabe?.visibility ?? kalender?.visibility ?? 'household')
    setUmfang('alle')
    setFreq(aktuelleRegel?.freq ?? '')
    setIntervall(aktuelleRegel?.interval ?? 1)
    setWochentage(aktuelleRegel?.byweekday ?? [])
    setMonatstag(aktuelleRegel?.bymonthday?.[0] ?? '')
    setPosition(aktuelleRegel?.bysetpos ?? '')
    setEnde(aktuelleRegel?.until ? 'datum' : aktuelleRegel?.count ? 'anzahl' : 'nie')
    setUntil(aktuelleRegel?.until ?? '')
    setCount(aktuelleRegel?.count ?? 1)
    setFehler(null)
    setOffen(true)
  }

  const wiederholung = () =>
    freq
      ? {
          freq,
          interval: intervall,
          ...(wochentage.length ? { byweekday: wochentage } : {}),
          ...(monatstag !== '' ? { bymonthday: [monatstag] } : {}),
          ...(position !== '' ? { bysetpos: position } : {}),
          ...(ende === 'datum' && until ? { until } : {}),
          ...(ende === 'anzahl' ? { count } : {}),
        }
      : null

  async function speichern(loeschen: boolean) {
    if (loeschen) {
      const erlaubt = await bestaetige({
        titel: serie ? 'Serie archivieren?' : 'Eintrag archivieren?',
        text: serie ? `Die ganze Serie „${titel}“ archivieren?` : `„${titel}“ archivieren?`,
        bestaetigen: 'Archivieren',
        gefahr: true,
      })
      if (!erlaubt) return
    }
    setLaeuft(true)
    setFehler(null)
    try {
      if (loeschen) await schicke('DELETE', `/api/${modul}/${id}`)
      else if (modul === 'tasks' && aufgabe) {
        await schicke('PATCH', `/api/tasks/${id}`, {
          title: name.trim(),
          notes: notes.trim() || null,
          kind: art,
          dueOn: datum || null,
          dueTime: zeit || null,
          priority: prioritaet,
          visibility: sichtbarkeit,
          assignees: auswahl,
          recur: wiederholung(),
        })
      } else if (kalender) await kalenderSpeichern(kalender)
      await Promise.all(
        [modul, 'dashboard', 'activity'].map((key) => qc.invalidateQueries({ queryKey: [key] })),
      )
      setOffen(false)
    } catch (e) {
      setFehler(e instanceof Error ? e.message : 'Änderung fehlgeschlagen.')
    } finally {
      setLaeuft(false)
    }
  }

  async function kalenderSpeichern(felder: KalenderFelder) {
    const patch = { title: name.trim(), notes: notes.trim() || null, location: ort.trim() || null }
    if (umfang === 'diese' && serie) {
      const zeitGeaendert =
        !ganztaegig &&
        (felder.allDayOn !== null ||
          datum !== felder.displayDate ||
          zeit !== startZeit(undefined, felder) ||
          dauer !== dauerMinuten(felder))
      await schicke(
        'PATCH',
        `/api/calendar/${id}/occurrence/${felder.occurrenceDate}`,
        zeitGeaendert
          ? {
              status: 'moved',
              movedToStart: zeitInZone(datum, `${zeit}:00`, felder.timezone).toISOString(),
              movedToEnd: new Date(
                zeitInZone(datum, `${zeit}:00`, felder.timezone).getTime() + dauer * 60000,
              ).toISOString(),
              patch,
            }
          : {
              status: 'modified',
              patch: ganztaegig
                ? { ...patch, allDayOn: datum, allDayBis: bisDatum || null }
                : patch,
            },
      )
      return
    }
    const body: Record<string, unknown> = {
      ...patch,
      visibility: sichtbarkeit,
      participants: auswahl,
      ...(umfang === 'folgende' ? {} : { recur: wiederholung() }),
      ...(ganztaegig
        ? { startsAt: null, endsAt: null, allDayOn: datum, allDayBis: bisDatum || null }
        : zeitwerte(datum, zeit, dauer, felder.timezone)),
      ...(serie && umfang === 'folgende'
        ? { scope: 'diese_und_folgende', abDatum: felder.occurrenceDate }
        : {}),
    }
    await schicke('PATCH', `/api/calendar/${id}`, body)
  }

  return (
    <div className="eintrag-aktionen">
      <Button variant="text" onClick={detailsUmschalten}>
        Details
      </Button>
      <Sheet
        offen={offen}
        titel={titel}
        onSchliessen={() => {
          setOffen(false)
        }}
      >
        <form
          className="stack-4"
          onSubmit={(e) => {
            e.preventDefault()
            void speichern(false)
          }}
        >
          {serie && modul === 'calendar' ? (
            <Select
              id={`${feldId}-umfang`}
              label="Änderung gilt für"
              wert={umfang}
              onWert={(wert) => {
                setUmfang(wert as Umfang)
                datumAendern(
                  wert === 'alle'
                    ? (kalender?.wiederholung?.startsOn ?? kalender?.displayDate ?? '')
                    : (kalender?.displayDate ?? ''),
                )
              }}
            >
              <option value="alle">Ganze Serie</option>
              <option value="diese">Nur diesen Termin</option>
              <option value="folgende">Diesen und alle folgenden</option>
            </Select>
          ) : null}
          {serie && modul === 'tasks' ? (
            <p className="unterzeile">Änderungen gelten für die ganze Aufgabenserie.</p>
          ) : null}

          <TextField
            id={`${feldId}-titel`}
            label="Titel"
            wert={name}
            onWert={setName}
            maxLength={200}
            required
            disabled={!edit || laeuft}
          />
          <div className="tf">
            <textarea
              id={`${feldId}-notizen`}
              className="tf__eingabe tf__eingabe--mehrzeilig"
              value={notes}
              placeholder=" "
              rows={3}
              onChange={(e) => setNotes(e.target.value)}
              disabled={!edit || laeuft}
            />
            <label className="tf__label" htmlFor={`${feldId}-notizen`}>
              Notizen
            </label>
          </div>

          {aufgabe ? (
            <>
              <div className="reihe reihe--felder">
                <Select
                  id={`${feldId}-art`}
                  label="Art"
                  wert={art}
                  onWert={(w) => {
                    setArt(w as 'todo' | 'chore')
                  }}
                >
                  <option value="todo">Aufgabe</option>
                  <option value="chore">Haushaltsaufgabe</option>
                </Select>
                <TextField
                  id={`${feldId}-faellig`}
                  label="Fällig am"
                  type="date"
                  wert={datum}
                  onWert={setDatum}
                  required={Boolean(freq)}
                />
                <TextField
                  id={`${feldId}-uhrzeit`}
                  label="Uhrzeit"
                  type="time"
                  wert={zeit}
                  onWert={setZeit}
                />
              </div>
              <div className="reihe reihe--felder">
                <Select
                  id={`${feldId}-prioritaet`}
                  label="Priorität"
                  wert={String(prioritaet)}
                  onWert={(w) => {
                    setPrioritaet(Number(w))
                  }}
                >
                  <option value="0">Normal</option>
                  <option value="1">Wichtig</option>
                  <option value="2">Dringend</option>
                  <option value="3">Sehr dringend</option>
                </Select>
                <Sichtbarkeit id={`${feldId}-sicht`} wert={sichtbarkeit} setzen={setSichtbarkeit} />
              </div>
            </>
          ) : null}

          {kalender ? (
            <>
              <TextField id={`${feldId}-ort`} label="Ort" wert={ort} onWert={setOrt} />
              <Switch checked={ganztaegig} onChange={setGanztaegig} label="Ganztägig" />
              <div className="reihe reihe--felder">
                <TextField
                  id={`${feldId}-datum`}
                  label="Datum"
                  type="date"
                  wert={datum}
                  onWert={datumAendern}
                  required
                />
                {ganztaegig ? (
                  <TextField
                    id={`${feldId}-bis`}
                    label="Bis einschließlich"
                    type="date"
                    min={datum}
                    wert={bisDatum}
                    onWert={setBisDatum}
                  />
                ) : (
                  <>
                    <TextField
                      id={`${feldId}-beginn`}
                      label="Beginn"
                      type="time"
                      wert={zeit}
                      onWert={setZeit}
                      required
                    />
                    <TextField
                      id={`${feldId}-dauer`}
                      label="Dauer (Min.)"
                      type="number"
                      min={5}
                      step={5}
                      wert={String(dauer)}
                      onWert={(w) => {
                        setDauer(Number(w))
                      }}
                    />
                  </>
                )}
              </div>
              {umfang !== 'diese' ? (
                <Sichtbarkeit id={`${feldId}-sicht`} wert={sichtbarkeit} setzen={setSichtbarkeit} />
              ) : null}
            </>
          ) : null}

          {!serie || umfang !== 'diese' ? (
            <Wiederholung
              feldId={feldId}
              freq={freq}
              setFreq={setFreq}
              intervall={intervall}
              setIntervall={setIntervall}
              wochentage={wochentage}
              setWochentage={setWochentage}
              monatstag={monatstag}
              setMonatstag={setMonatstag}
              position={position}
              setPosition={setPosition}
              ende={ende}
              setEnde={setEnde}
              until={until}
              setUntil={setUntil}
              count={count}
              setCount={setCount}
            />
          ) : null}

          {edit && (modul === 'tasks' || umfang !== 'diese') ? (
            <PersonenAuswahl wert={auswahl} setzen={setAuswahl} />
          ) : null}

          {fehler ? (
            <p className="fehler" role="alert">
              {fehler}
            </p>
          ) : null}

          <div className="blatt__fuss-inline">
            {remove ? (
              <Button
                variant="text"
                className="btn--gefahr"
                disabled={laeuft}
                onClick={() => void speichern(true)}
              >
                Archivieren
              </Button>
            ) : (
              <span />
            )}
            {edit ? (
              <Button type="submit" laeuft={laeuft} disabled={!name.trim()}>
                Speichern
              </Button>
            ) : null}
          </div>
        </form>
      </Sheet>
    </div>
  )
}

function Sichtbarkeit({
  id,
  wert,
  setzen,
}: {
  id: string
  wert: string
  setzen: (wert: string) => void
}) {
  return (
    <Select id={id} label="Sichtbarkeit" wert={wert} onWert={setzen}>
      <option value="household">Ganzer Haushalt</option>
      <option value="private">Privat</option>
      <option value="restricted">Nur Beteiligte</option>
    </Select>
  )
}

function Wiederholung(p: {
  feldId: string
  freq: string
  setFreq: (v: string) => void
  intervall: number
  setIntervall: (v: number) => void
  wochentage: number[]
  setWochentage: (v: number[]) => void
  monatstag: number | ''
  setMonatstag: (v: number | '') => void
  position: number | ''
  setPosition: (v: number | '') => void
  ende: 'nie' | 'datum' | 'anzahl'
  setEnde: (v: 'nie' | 'datum' | 'anzahl') => void
  until: string
  setUntil: (v: string) => void
  count: number
  setCount: (v: number) => void
}) {
  const id = (name: string): string => `${p.feldId}-${name}`
  return (
    <fieldset className="feldgruppe">
      <legend>Wiederholung</legend>
      <div className="reihe reihe--felder">
        <Select id={id('rhythmus')} label="Rhythmus" wert={p.freq} onWert={p.setFreq}>
          <option value="">Keine</option>
          <option value="daily">Täglich</option>
          <option value="weekly">Wöchentlich</option>
          <option value="monthly">Monatlich</option>
          <option value="yearly">Jährlich</option>
        </Select>
        {p.freq ? (
          <TextField
            id={id('intervall')}
            label="Intervall"
            type="number"
            min={1}
            wert={String(p.intervall)}
            onWert={(w) => {
              p.setIntervall(Math.max(1, Number(w)))
            }}
          />
        ) : null}
      </div>

      {p.freq === 'weekly' || p.freq === 'monthly' ? (
        <div className="stack-2">
          <span className="feldgruppe__titel">Wochentage</span>
          <div className="chips" role="group" aria-label="Wochentage">
            {WOCHENTAGE.map(([nr, name]) => (
              <Chip
                key={nr}
                gewaehlt={p.wochentage.includes(nr)}
                onClick={() => {
                  p.setWochentage(
                    p.wochentage.includes(nr)
                      ? p.wochentage.filter((x) => x !== nr)
                      : [...p.wochentage, nr].sort((a, b) => a - b),
                  )
                }}
              >
                {name}
              </Chip>
            ))}
          </div>
        </div>
      ) : null}

      {p.freq === 'monthly' ? (
        <div className="reihe reihe--felder">
          <TextField
            id={id('monatstag')}
            label="Monatstag"
            type="number"
            min={-1}
            max={31}
            wert={p.monatstag === '' ? '' : String(p.monatstag)}
            onWert={(w) => {
              p.setMonatstag(w ? Number(w) : '')
            }}
            hilfe="z. B. 15 oder -1 für den letzten"
          />
          <Select
            id={id('position')}
            label="Position"
            wert={p.position === '' ? '' : String(p.position)}
            onWert={(w) => {
              p.setPosition(w ? Number(w) : '')
            }}
          >
            <option value="">Keine</option>
            <option value="1">Erster</option>
            <option value="2">Zweiter</option>
            <option value="3">Dritter</option>
            <option value="4">Vierter</option>
            <option value="-1">Letzter</option>
          </Select>
        </div>
      ) : null}

      {p.freq ? (
        <div className="reihe reihe--felder">
          <Select
            id={id('ende')}
            label="Ende"
            wert={p.ende}
            onWert={(w) => {
              p.setEnde(w as 'nie' | 'datum' | 'anzahl')
            }}
          >
            <option value="nie">Nie</option>
            <option value="datum">Am Datum</option>
            <option value="anzahl">Nach Anzahl</option>
          </Select>
          {p.ende === 'datum' ? (
            <TextField
              id={id('enddatum')}
              label="Enddatum"
              type="date"
              wert={p.until}
              onWert={p.setUntil}
              required
            />
          ) : null}
          {p.ende === 'anzahl' ? (
            <TextField
              id={id('anzahl')}
              label="Anzahl"
              type="number"
              min={1}
              wert={String(p.count)}
              onWert={(w) => {
                p.setCount(Math.max(1, Number(w)))
              }}
              required
            />
          ) : null}
        </div>
      ) : null}
    </fieldset>
  )
}
function startDatum(aufgabe?: AufgabenFelder, kalender?: KalenderFelder): string {
  return (
    aufgabe?.dueOn ??
    kalender?.wiederholung?.startsOn ??
    kalender?.allDayOn ??
    kalender?.displayDate ??
    ''
  )
}
function startZeit(aufgabe?: AufgabenFelder, kalender?: KalenderFelder): string {
  if (aufgabe) return aufgabe.dueTime?.slice(0, 5) ?? ''
  return kalender?.startsAt
    ? inZone(new Date(kalender.startsAt), kalender.timezone).uhrzeit.slice(0, 5)
    : '09:00'
}
function dauerMinuten(kalender?: KalenderFelder): number {
  return kalender?.startsAt && kalender.endsAt
    ? Math.max(5, Math.round((Date.parse(kalender.endsAt) - Date.parse(kalender.startsAt)) / 60000))
    : 60
}
function zeitwerte(datum: string, zeit: string, dauer: number, timezone: string) {
  const start = zeitInZone(datum, `${zeit}:00`, timezone)
  return {
    startsAt: start.toISOString(),
    endsAt: new Date(start.getTime() + dauer * 60000).toISOString(),
    allDayOn: null,
    allDayBis: null,
  }
}
